import React from 'react';
import {Map} from 'immutable';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Plugins, Atom} from '@packages/systems/wfdl';
import type {AtomRenderProps} from '@packages/systems/wfdl';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_ADD_TO_CART_PILL_GROUP,
  DATA_ATTR_COMMERCE_OPTION_SET_ID,
} from '@packages/systems/commerce/constants';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';

import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {EvaluatedNodeData, getChildren} from '@packages/utilities/data-utils';

const renderOptions = (
  {renderChild, context, data}: AtomRenderProps,
  options: [
    {
      id: string;
      name: string;
      slug?: string;
    },
  ],
  optionSetId: string
) => {
  const renderOptionWithContext = (
    option: {
      id: string;
      name: string;
      slug?: string;
    },
    i: number
  ) => {
    return getChildren(data).map((child: EvaluatedNodeData) => {
      const newContext = context
        .set('data', option)
        .set('optionSetId', optionSetId);
      return renderChild(child, newContext, option.id, i);
    });
  };

  return options.length > 0 ? options.map(renderOptionWithContext) : null;
};

const CommerceAddToCartOptionPillGroup = Atom.createRender(
  (props: AtomRenderProps) => {
    const {attributes, classes, context, generateAttributes} = props;
    const data = context.get('data');
    const shouldRender = context.get('selectorType') === 'pills';
    if (data && shouldRender) {
      const {id: optionSetId, name: optionSetName, enum: optionSetEnum} = data;
      const options = renderOptions(props, optionSetEnum, optionSetId);

      const newAttributes = (attributes || Map()).merge(
        Map({
          id: 'option-set-' + optionSetId,
          [DATA_ATTR_NODE_TYPE]: NODE_TYPE_COMMERCE_ADD_TO_CART_PILL_GROUP,
          [DATA_ATTR_COMMERCE_OPTION_SET_ID]: optionSetId,
        })
      );

      return (
        <div
          {...generateAttributes(newAttributes, classes)}
          role="radiogroup"
          aria-label={optionSetName}
        >
          {options}
        </div>
      );
    }
    return null;
  }
);

const config = createIntegratedAtom({
  displayName: 'Button Group',
  showEmpty: false,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Div')),
  },
  fields: {children: TListOfElement},
  constraints: {
    pinToParent: true,
    parent: [
      {
        is: 'CommerceAddToCartOption',
        rule: 'ExactlyOne',
      },
    ],
    descendants: [
      {
        is: 'CommerceAddToCartOptionPill',
        rule: 'ExactlyOne',
      },
    ],
    children: [
      {
        is: 'CommerceAddToCartOptionPill',
        name: 'Non-Button',
        rule: 'RequireOnly',
        messages: {
          paste:
            'Paste this element inside the Button element to add it to each Button item.',
          move: 'Move this element inside the Button element to add it to each Button item.',
        },
      },
    ],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          display: 'flex',
          marginBottom: px(10),
        },
      },
    },
  },
  render: CommerceAddToCartOptionPillGroup,
});

export default config;
